import { Component, computed, effect, Input, input, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import {
  ApexAxisChartSeries,
} from 'ng-apexcharts';
import { IChartOptions, IHistoryChartInput, IInstrumentChartPoint } from 'src/models/charts.model';
import { ECurrencyCode } from 'src/models/common.model';
import { LoggerService } from 'src/services/logger.service';
import { generateLastDeposit, getChartFill, getChartStroke, HISTORY_DEVELOPMENT_CHART_CONFIG, updateHistoryDevelopmentChartOverlay } from 'src/util/charts.config';

@Component({
  selector: 'liberro-history-development-chart',
  standalone: false,
  templateUrl: './liberro-history-development-chart.component.html',
  styleUrl: './liberro-history-development-chart.component.less'
})
export class LiberroHistoryDevelopmentChartComponent {
    private translateService = inject(TranslateService);
    private loggerService = inject(LoggerService);

    @Input({ required: true }) public currencyCode!: ECurrencyCode;
    public chartData = input.required<IHistoryChartInput | null>();
    public hideAmounts = input<boolean>(false);
    public chartOptions?: IChartOptions;
    public noDataToDisplay = computed(() => this.shouldDisplayChart());

    public constructor() {
    effect(() => {
        this.updateChart();
    });
    }

    private shouldDisplayChart(): boolean {
        const chartData = this.chartData();
        return !!(chartData &&
            chartData?.valuesInTime &&
            chartData?.valuesInTime?.length === 0 &&
            chartData?.deposits &&
            chartData?.deposits?.length === 0 ||
            chartData &&
            chartData?.percentsInTime &&
            chartData?.percentsInTime?.length === 0)
    }

    public updateChart(): void {
        let series: ApexAxisChartSeries = [];
        const chartInputData = this.chartData();

        if(chartInputData?.valuesInTime && chartInputData?.deposits) {
            series = [
                {
                    name: this.translateService.instant('charts.value'),
                    type: 'area',
                    data: chartInputData.valuesInTime.map(item => ({
                        x: item.x,
                        y: item.y,
                    }))
                },
            ];

            if(chartInputData.deposits.length) {
                // to make deposits line appear on same start as prices in time
                const initialDepositChartValue = {
                    x: chartInputData.valuesInTime?.[0]?.x ?? 0,
                    y: Math.min(...chartInputData.deposits.map(value => value.y)),
                    fillColor: 'transparent',
                }
                series.push({
                    name: this.translateService.instant('charts.deposit'),
                    type: 'line',
                    data: [
                    initialDepositChartValue,
                    ...chartInputData!.deposits!.map(item => ({
                        x: item.x,
                        y: item.y,
                        metadata: {
                            depositValue: item.depositValue,
                            depositTooltipName: this.translateService.instant((item.depositValue ?? 0) > 0 ? 'charts.deposit' : 'charts.withdrawal'),
                        }
                    }))]
                });
                try {
                    //workaround for deposits line to end same as values in time
                    ((series[1].data as ApexAxisChartSeries[number]['data'][number][]))?.push(generateLastDeposit(series));
                } catch (error) {
                    this.loggerService.error(error);
                }
            }
        } else if (chartInputData?.percentsInTime) {
            series = [
                {
                    name: this.translateService.instant('charts.value'),
                    type: 'area',
                    data: chartInputData!.percentsInTime!.map(item => ({
                        x: item.x,
                        y: item.y,
                        metadata: {
                            isPercent: true,
                            currency: this.currencyCode,
                        }
                    }))
                },
            ];
        }

        const mainChartSerie = series[0].data as IInstrumentChartPoint[];
        const chartColor = mainChartSerie[0]?.y > mainChartSerie[mainChartSerie.length - 1]?.y ? '#4F4F4F' : '#3D8146';

        const hideAmounts = this.hideAmounts();
        this.chartOptions = {
            ...HISTORY_DEVELOPMENT_CHART_CONFIG as IChartOptions,
            stroke: getChartStroke(chartColor),
            fill: getChartFill(chartColor),
            markers: {
                ...HISTORY_DEVELOPMENT_CHART_CONFIG.markers,
                colors: ['transparent', chartColor],
            },
            series,
            tooltip: {
                ...HISTORY_DEVELOPMENT_CHART_CONFIG.tooltip,
                custom: (input) => {
                    updateHistoryDevelopmentChartOverlay(input, {
                        currencyOverride: this.currencyCode,
                        toTwoDecimal: true,
                        freezeAtEdge: true,
                        hideAmounts,
                    });
                    return '';
                },
            },
        };
    }
}
